import { Box, Flex, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';

/** About V2 sections share the Thmanyah type ramp (see {@link HeroSection}). */
const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', sans-serif";

export interface RoadmapCardProps {
  /** Step number shown faded in the top-start corner. */
  number: number | string;
  /** Card title. */
  title: ReactNode;
  /** Supporting description. */
  body: ReactNode;
  /**
   * Card background — a solid colour or any CSS `background` value (e.g. a layered
   * gradient). Each step uses a distinct colour.
   */
  color: string;
}

/**
 * Roadmap card (TZ-383) for the "المستقبل اللي نبنيه" section: a coloured vision card
 * with a faded step number in the top-start corner and the title + body pinned to the
 * bottom (distributed via space-between). RTL; the `color` prop drives the background.
 *
 * The three design colours are all dark/saturated, so the text is always white for
 * legibility, with a subtle top-start highlight for depth.
 */
export const RoadmapCard = ({ number, title, body, color }: RoadmapCardProps) => (
  <Flex
    dir='rtl'
    direction='column'
    justify='space-between'
    position='relative'
    overflow='hidden'
    gap={{ base: '32px', md: '48px' }}
    textAlign='start'
    h='100%'
    minH={{ base: '260px', md: '313px' }}
    p='24px'
    borderRadius='24px'
    // `color` may be a solid colour or a full CSS background (e.g. layered gradients);
    // set via literal `background` so Chakra's gradient-token parser leaves it alone.
    sx={{ background: color }}
  >
    {/* Subtle top-start sheen for depth (matches the design's soft highlight). Uses a
        literal `background` so Chakra's gradient-token parser doesn't choke on the % stops. */}
    <Box
      position='absolute'
      inset={0}
      sx={{
        background:
          'radial-gradient(100% 80% at 12% 8%, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0) 55%)',
      }}
    />

    <Text
      as='span'
      // LTR so the leading dot stays on the left of the digit (".3").
      dir='ltr'
      position='relative'
      zIndex={1}
      alignSelf='flex-start'
      m={0}
      color='rgba(255, 255, 255, 0.4)'
      fontFamily={FONT_FAMILY}
      fontWeight='700'
      fontSize={{ base: '20px', md: '24px' }}
      lineHeight='1'
    >
      .{number}
    </Text>

    <Box position='relative' zIndex={1}>
      <Text
        as='h3'
        m={0}
        mb='8px'
        color='white'
        fontFamily={FONT_FAMILY}
        fontWeight='800'
        fontSize={{ base: '20px', md: '22px' }}
        lineHeight='130%'
      >
        {title}
      </Text>
      <Text
        as='p'
        m={0}
        color='rgba(255, 255, 255, 0.75)'
        fontFamily={FONT_FAMILY}
        fontWeight='400'
        fontSize={{ base: '14px', md: '15px' }}
        lineHeight='175%'
      >
        {body}
      </Text>
    </Box>
  </Flex>
);
